"use client";
import { useTranslations } from "@zapier/i18n";

import {
  useAccountActionPermissions,
  useSplitTreatments,
  useSupportedAssets,
} from "@zapier/client";
import { BetaPageHeader, Button, VisuallyHidden } from "@zapier/design-system";
import { Icon } from "@zapier/zinnia-icons";
import {
  FolderSearchInteractionOptions,
  OwnerFilter,
  SearchInput,
  useEmitFolderSearchInteractionEvent,
  useRouterParams,
  DefaultViewButton,
} from "@zapier/shared";
import { AssetTypes, getFolderUrl } from "@zapier/utils";
import { TrashButton } from "@zapier/zaps";
import { useCallback, useEffect, useRef, useState } from "react";
import styles from "./FoldersHeader.module.css";

import { FolderCreationModal } from "../../FolderCreationModal";
import { FoldersTypeFilter } from "./FoldersTypeFilter";

interface Props {
  totalResults?: number;
  isLoading?: boolean;
}

export function FoldersHeader({ totalResults, isLoading }: Props = {}) {
  const t = useTranslations("folders-header");
  const tButton = useTranslations("folder-creation-button");

  const { emit } = useEmitFolderSearchInteractionEvent();
  const { search: searchQuery, setParams } = useRouterParams();
  const { isMultiUserAccount, isSharingEnabled } =
    useAccountActionPermissions();
  const { isMPFEnabled, isLoading: isMPFLoading } = useSupportedAssets();
  const { isZapAndCanvasUnifiedTrashEnabled } = useSplitTreatments();
  const [pageTitle, setPageTitle] = useState("");
  const [isFolderCreationModalOpen, setIsFolderCreationModalOpen] =
    useState(false);
  const [announcement, setAnnouncement] = useState("");
  // The search query we last announced for. Seeded to the query present on first
  // render (see effect) so we never announce the initial page state — including
  // landing with a ?search= param — then updated on every committed change. Used
  // to dedupe so unrelated result-count changes (type filter, folder add/remove,
  // background refetch) don't re-announce the same query.
  const lastAnnouncedSearchReference = useRef<string | null>(null);

  const search = useCallback(
    (searchValue: string) => {
      emit({
        optionSelected: FolderSearchInteractionOptions.Keyword,
      });
      setParams({ search: searchValue }, ["page"]);
    },
    [emit, setParams],
  );

  useEffect(() => {
    if (!isMPFLoading) {
      setPageTitle(isMPFEnabled ? t("titles.folders") : t("titles.zaps"));
    }
  }, [isMPFLoading, isMPFEnabled, t]);

  // Announce the result count / no-results state whenever the committed search
  // query changes. Driving this off the URL-derived `searchQuery` (not off the
  // search handler) means it fires however the query changed — typing + Enter,
  // or clearing via the input's clear button, which calls setParams directly and
  // never invokes onSearch. Folder search is in-memory, so `totalResults` already
  // reflects the committed query in the same render. We seed the baseline on the
  // first run so the initial page state (incl. landing with a ?search= param) is
  // never announced, dedupe on the query so unrelated count changes stay quiet,
  // and skip while results are still loading.
  useEffect(() => {
    const currentQuery = searchQuery ?? "";
    if (lastAnnouncedSearchReference.current === null) {
      lastAnnouncedSearchReference.current = currentQuery;
      return;
    }
    if (isLoading || currentQuery === lastAnnouncedSearchReference.current) {
      return;
    }
    lastAnnouncedSearchReference.current = currentQuery;
    setAnnouncement(
      currentQuery
        ? t("announcement.search-results", {
            count: totalResults ?? 0,
            query: currentQuery,
          })
        : t("announcement.search-cleared", { count: totalResults ?? 0 }),
    );
  }, [searchQuery, totalResults, isLoading, t]);

  return (
    <>
      <BetaPageHeader
        title={pageTitle}
        actions={
          <>
            {/* This page has no mobile nav fallback for Trash, so surface it
                here (mobile-only) once unified trash is on. */}
            {isZapAndCanvasUnifiedTrashEnabled && (
              <TrashButton href={getFolderUrl("trash")} mobileOnly />
            )}
            <Button
              variant="primary"
              onPress={() => setIsFolderCreationModalOpen(true)}
              iconPosition="start"
              data-testid="folders-header-create-button"
            >
              <Icon name="formAdd" />
              {tButton("buttons.create")}
            </Button>
          </>
        }
        additionalContent={
          <div className={styles.root}>
            {/* Polite SR announcement of the search result count / no-results. */}
            <VisuallyHidden>
              <div role="status" aria-live="polite" aria-atomic="true">
                {announcement}
              </div>
            </VisuallyHidden>
            <SearchInput
              placeholder={t("search.placeholder-name")}
              onSearch={search}
            />

            <FoldersTypeFilter
              totalResults={totalResults}
              isLoading={isLoading}
            />

            {isMultiUserAccount && (
              <div className={styles.ownerFilter}>
                <OwnerFilter
                  assetType={AssetTypes.Folder}
                  isLabelHidden={true}
                />
              </div>
            )}

            <DefaultViewButton />
          </div>
        }
      />

      <FolderCreationModal
        isOpen={isFolderCreationModalOpen}
        setIsOpen={setIsFolderCreationModalOpen}
        isSharedSelectable={isSharingEnabled}
      />
    </>
  );
}
